import React, { FC, MouseEvent, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown, Menu, Tag } from 'antd';
import { AssetVersion } from './types';
import styles from './index.module.css';

type Props = {
  versions: AssetVersion[];
  selected: AssetVersion | 'all';
  onSelect: (version: AssetVersion) => void;
  onSelectAll?: () => void;
  showAllOption?: boolean;
  container?: HTMLElement;
  onUploadNewVersion?: (file: File) => void;
  onSyncNewVersion?: () => void;
  accept?: string;
};

const VersionSelector: FC<Props> = ({
  versions,
  selected,
  onSelect,
  onSelectAll,
  showAllOption,
  container,
  onUploadNewVersion,
  onSyncNewVersion,
  accept
}) => {
  const versionInputRef = useRef<HTMLInputElement>(null);
  const stopPropagation = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const openFilePicker = () => versionInputRef.current?.click();

  const footerAction = onSyncNewVersion
    ? { key: 'sync', label: 'Sync new version', onClick: onSyncNewVersion }
    : onUploadNewVersion
    ? { key: 'upload', label: 'Upload new version', onClick: openFilePicker }
    : null;

  return (
    <>
      {onUploadNewVersion &&
        createPortal(
          <input
            ref={versionInputRef}
            type="file"
            accept={accept}
            className={styles.versionFileInput}
            onChange={event => {
              const file = event.target.files?.[0];
              if (file) onUploadNewVersion(file);
              event.target.value = '';
            }}
          />,
          container || document.body
        )}
      <Dropdown
        trigger={['click']}
        getPopupContainer={() => container || document.body}
        overlay={
          <Menu>
            {showAllOption && (
              <Menu.Item
                key="all"
                onClick={({ domEvent }) => {
                  domEvent.preventDefault();
                  domEvent.stopPropagation();
                  onSelectAll?.();
                }}
              >
                All Versions
              </Menu.Item>
            )}
            {[...versions].reverse().map(version => (
              <Menu.Item
                key={version.version}
                onClick={({ domEvent }) => {
                  domEvent.preventDefault();
                  domEvent.stopPropagation();
                  onSelect(version);
                }}
              >
                Version {version.version}
              </Menu.Item>
            ))}
            {footerAction && (
              <>
                {versions.length > 0 && <Menu.Divider />}
                <Menu.Item
                  key={footerAction.key}
                  onMouseDown={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (footerAction.key === 'upload') openFilePicker();
                  }}
                  onClick={({ domEvent }) => {
                    domEvent.preventDefault();
                    domEvent.stopPropagation();
                    if (footerAction.key !== 'upload') footerAction.onClick();
                  }}
                >
                  {footerAction.label}
                </Menu.Item>
              </>
            )}
          </Menu>
        }
      >
        <Tag className={styles.versionTag} onClick={stopPropagation}>
          {selected === 'all' ? 'All Versions' : `Version ${selected.version}`}
        </Tag>
      </Dropdown>
    </>
  );
};

export default VersionSelector;
